import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Skeleton
} from '@mui/material';
import {
  SyncOutlined as SyncIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';

export default function SyncOperationsTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch sync status first to initialize correctly
  const {
    data: syncStatusData,
    isLoading: isLoadingSyncStatus,
    refetch: refetchSyncStatus,
    error: syncStatusError
  } = useQuery({
    queryKey: ['trackingSyncStatus', 'trackingSyncOperations'],
    queryFn: async () => {
      try {
        const response = await api.get('/tracking-sync/status');
        return response.data.data;
      } catch (error) {
        console.error('Error in tracking sync status query:', error);
        // If it's a 404, return a default status
        if (error.response?.status === 404) {
          return { isRunning: false, activeSync: null };
        }
        throw error;
      }
    },
    refetchOnWindowFocus: true,
    refetchInterval: 5000, // Poll every 5 seconds
    enabled: true, // Always enabled
    staleTime: 0, // Always consider data stale to force a refresh
    retry: (failureCount, error) => {
      // Don't retry on 404 errors
      if (error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    }
  });

  // Initialize based on current sync status
  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null // 'whole' or 'updated'
  });

  // Update the sync status whenever data changes
  useEffect(() => {
    if (syncStatusError && syncStatusError.response?.status !== 404) {
      console.error('Error fetching tracking sync status:', syncStatusError);
      setIsSyncInProgress(false);
      return;
    }

    const syncRunning = syncStatusData?.isRunning === true;
    setIsSyncInProgress(syncRunning);
  }, [syncStatusData, syncStatusError]);

  // Fetch sync settings
  const {
    data: syncSettings,
    isLoading: isLoadingSettings
  } = useQuery({
    queryKey: ['trackingSyncStatus', 'trackingSyncSettingsStatus'],
    queryFn: async () => {
      try {
        const response = await api.get('/tracking-sync/settings');
        return response.data.data;
      } catch (error) {
        console.error('Error fetching tracking sync settings:', error);
        // If settings don't exist, return empty settings
        if (error.response?.status === 404) {
          return {
            whole_tracking_query: '',
            updated_tracking_query: '',
            columns: '',
            query_batch_size: 100,
            is_notify_customer: false,
            read_endpoint: '/api/data/read'
          };
        }
        throw error;
      }
    },
    retry: (failureCount, error) => {
      // Don't retry on 404 errors
      if (error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 0
  });

  // Fetch field mappings
  const {
    data: fieldMappings,
    isLoading: isLoadingFieldMappings
  } = useQuery({
    queryKey: ['trackingFieldMappings'],
    queryFn: async () => {
      const response = await api.get('/tracking-sync/field-mappings');
      return response.data.data;
    },
    staleTime: 0
  });

  // Check if all required settings are filled
  const areRequiredSettingsFilled = () => {
    if (!syncSettings) return false;

    const requiredFields = [
      'whole_tracking_query',
      'updated_tracking_query',
      'columns',
      'query_batch_size',
      'read_endpoint'
    ];

    return requiredFields.every(field => {
      const value = syncSettings[field];
      if (field === 'query_batch_size') {
        return value !== null && value !== undefined && value !== '' && value > 0;
      }
      return value !== null && value !== undefined && value !== '' && String(value).trim() !== '';
    });
  };

  // Check if field mappings exist
  const areFieldMappingsConfigured = () => {
    return fieldMappings && fieldMappings.length > 0;
  };

  // Start whole tracking sync mutation
  const startWholeTrackingSyncMutation = useMutation({
    mutationFn: async () => {
      const batchSize = syncSettings?.query_batch_size || 100;
      const response = await api.post('/tracking-sync/sync/whole-tracking', {
        query_batch_size: batchSize
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Whole tracking sync started successfully');
      queryClient.invalidateQueries({ queryKey: ['syncLogs'] });
      refetchSyncStatus();
      setIsSyncInProgress(true);
    },
    onError: (error) => {
      toast.error(`Failed to start whole tracking sync: ${error.response?.data?.message || error.message}`);
    }
  });

  // Start updated tracking sync mutation
  const startUpdatedTrackingSyncMutation = useMutation({
    mutationFn: async () => {
      const batchSize = syncSettings?.query_batch_size || 100;
      const response = await api.post('/tracking-sync/sync/updated-tracking', {
        query_batch_size: batchSize
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success("Today's updated tracking sync started successfully");
      queryClient.invalidateQueries({ queryKey: ['syncLogs'] });
      refetchSyncStatus();
      setIsSyncInProgress(true);
    },
    onError: (error) => {
      toast.error(`Failed to start updated tracking sync: ${error.response?.data?.message || error.message}`);
    }
  });

  return (
    <>
      <Grid container spacing={3}>
        {isLoadingSyncStatus || isLoadingSettings || isLoadingFieldMappings ? (
          <>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                    <Skeleton variant="text" width={200} />
                  </Box>
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="100%" />

                  <Skeleton
                    variant="rectangular"
                    sx={{
                      mt: 2,
                      mb: 2,
                      height: 60,
                      borderRadius: 1,
                      bgcolor: 'rgba(41, 182, 246, 0.1)'
                    }}
                  />

                  <Skeleton
                    variant="rectangular"
                    height={36}
                    sx={{
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      opacity: 0.2
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                    <Skeleton variant="text" width={200} />
                  </Box>
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="100%" />

                  <Skeleton
                    variant="rectangular"
                    sx={{
                      mt: 2,
                      mb: 2,
                      height: 60,
                      borderRadius: 1,
                      bgcolor: 'rgba(41, 182, 246, 0.1)'
                    }}
                  />

                  <Skeleton
                    variant="rectangular"
                    height={36}
                    sx={{
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      opacity: 0.2
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box mt={2} display="flex" justifyContent="center" sx={{ pb: { xs: '50px'} }}>
                <Skeleton
                  variant="rectangular"
                  width={180}
                  height={36}
                  animation="wave"
                  sx={{ borderRadius: 1, bgcolor: 'secondary.light', opacity: 0.3 }}
                />
              </Box>
            </Grid>
          </>
        ) : (
          <>
            {/* Sync in progress warning banner */}
            {isSyncInProgress && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 0 }}>
                  Tracking synchronization is currently in progress. Sync operations are disabled until the sync completes.
                </Alert>
              </Grid>
            )}

            {!areRequiredSettingsFilled() && (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => navigate('/tracking-sync/settings')}
                    >
                      Configure Settings
                    </Button>
                  }
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Configuration Required
                  </Typography>
                  <Typography variant="body2">
                    Please configure all required settings before running sync operations.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {!areFieldMappingsConfigured() && (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => navigate('/tracking-sync/mapping')}
                    >
                      Configure Mappings
                    </Button>
                  }
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Field Mappings Required
                  </Typography>
                  <Typography variant="body2">
                    Please configure field mappings to map ERP data to Shopify fields before running sync operations.
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <SyncIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h3">
                      Whole Tracking Sync
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    Sync all available tracking information from your ERP system to Shopify. This operation will create,
                    update, or skip tracking records based on their existence in Shopify.
                  </Typography>

                  <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '12px'} }}>
                    This operation may take a long time depending on the number of products in your ERP system.
                    You can check the sync progress in the Sync Logs section.
                  </Alert>

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="medium"
                    startIcon={startWholeTrackingSyncMutation.isPending ?
                      <CircularProgress size={20} color="inherit" /> :
                      <SyncIcon />
                    }
                    onClick={() => setConfirmDialog({ open: true, type: 'whole' })}
                    disabled={isSyncInProgress || startWholeTrackingSyncMutation.isPending || !areRequiredSettingsFilled() || !areFieldMappingsConfigured()}
                    sx={{ mt: 'auto', width: '100% !important' }}
                  >
                    {startWholeTrackingSyncMutation.isPending ? 'Starting Sync...' : 'Start Whole Tracking Sync'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <RefreshIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h3">
                      Today's Updated Tracking Sync
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    Sync only the tracking records that have been updated in your ERP system today. This is a faster
                    operation for regular updates.
                  </Typography>

                  <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '12px'} }}>
                    This operation only syncs tracking records that have been modified or created today in your
                    ERP system. You can check the sync progress in the Sync Logs section.
                  </Alert>

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="medium"
                    startIcon={startUpdatedTrackingSyncMutation.isPending ?
                      <CircularProgress size={20} color="inherit" /> :
                      <RefreshIcon />
                    }
                    onClick={() => setConfirmDialog({ open: true, type: 'updated' })}
                    disabled={isSyncInProgress || startUpdatedTrackingSyncMutation.isPending || !areRequiredSettingsFilled() || !areFieldMappingsConfigured()}
                    sx={{ mt: 'auto', width: '100% !important' }}
                  >
                    {startUpdatedTrackingSyncMutation.isPending ? 'Starting Sync...' : "Start Today's Updated Tracking Sync"}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" mt={2} sx={{ pb: { xs: '50px'} }}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<HistoryIcon />}
                  onClick={() => navigate('/sync-logs?syncType=tracking&direction=erp_to_shopify')}
                  size="medium"
                >
                  View Sync Logs
                </Button>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.type === 'whole' ? 'Start Whole Tracking Sync?' : "Start Today's Updated Tracking Sync?"}
        message={confirmDialog.type === 'whole'
          ? 'Are you sure you want to start a whole tracking sync? This may take a long time depending on the number of records.'
          : 'Are you sure you want to start today\'s updated tracking sync? This will only sync records updated or created today.'}
        confirmText={confirmDialog.type === 'whole' ? 'Start Whole Sync' : 'Start Updated Sync'}
        cancelText="Cancel"
        confirmColor="primary"
        isLoading={
          (confirmDialog.type === 'whole' && startWholeTrackingSyncMutation.isPending) ||
          (confirmDialog.type === 'updated' && startUpdatedTrackingSyncMutation.isPending)
        }
        onCancel={() => setConfirmDialog({ open: false, type: null })}
        onConfirm={() => {
          if (confirmDialog.type === 'whole') {
            startWholeTrackingSyncMutation.mutate(undefined, {
              onSettled: () => setConfirmDialog({ open: false, type: null })
            });
          } else if (confirmDialog.type === 'updated') {
            startUpdatedTrackingSyncMutation.mutate(undefined, {
              onSettled: () => setConfirmDialog({ open: false, type: null })
            });
          }
        }}
      />
    </>
  );
}
